function safeStr(value) {
  if (value == null) return '';
  return String(value).trim();
}

function parseJsonLayers(value, maxDepth = 8) {
  let current = value;

  for (let i = 0; i < maxDepth; i += 1) {
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) return null;

    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }

  return current;
}

function toIsoStringSafe(value) {
  if (!value) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export function overlapsIso(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA < endB && startB < endA;
}

export function parseReservationCarSubstitutions(value) {
  const parsed = parseJsonLayers(value);
  if (!Array.isArray(parsed)) return [];

  const normalized = parsed
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;

      const effectiveAt = toIsoStringSafe(
        entry.effectiveAt ||
          entry.switchAt ||
          entry.substitutedAt ||
          entry.createdAt ||
          entry.signedAt
      );
      const toCarId = safeStr(entry.toCarId || entry.replacementCarId || entry.carId);

      if (!effectiveAt || !toCarId) return null;

      return {
        id: safeStr(entry.id || entry.contractId || entry.handoverId || `sub-${index + 1}`),
        effectiveAt,
        reason: safeStr(entry.reason || entry.notes),
        fromCarId: safeStr(entry.fromCarId || entry.previousCarId || entry.currentCarId),
        fromCarName: safeStr(entry.fromCarName || entry.previousCarName || entry.currentCarName),
        fromCarPlate: safeStr(entry.fromCarPlate || entry.previousCarPlate || entry.currentCarPlate),
        toCarId,
        toCarName: safeStr(entry.toCarName || entry.replacementCarName || entry.carName),
        toCarPlate: safeStr(entry.toCarPlate || entry.replacementCarPlate || entry.carPlate),
        signedAt: toIsoStringSafe(entry.signedAt || entry.createdAt || entry.effectiveAt),
        filledByEmail: safeStr(entry.filledByEmail),
        contractId: safeStr(entry.contractId || entry.handoverId),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.effectiveAt < b.effectiveAt) return -1;
      if (a.effectiveAt > b.effectiveAt) return 1;
      return safeStr(a.id).localeCompare(safeStr(b.id));
    });

  const deduped = [];
  const seen = new Set();

  for (const entry of normalized) {
    const key = `${entry.effectiveAt}::${entry.fromCarId}::${entry.toCarId}::${entry.contractId || entry.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(entry);
  }

  return deduped;
}

function buildSegment({
  reservation,
  segmentIndex,
  carId,
  carName,
  carPlate,
  start,
  end,
  transition = null,
  isSubstitution = false,
}) {
  if (!carId || !start || !end || start >= end) return null;

  return {
    reservationId: reservation?.id || '',
    segmentIndex,
    carId,
    carName: carName || '',
    carPlate: carPlate || '',
    start,
    end,
    isSubstitution,
    transition,
  };
}

export function getReservationCarSegments(reservation) {
  const start = toIsoStringSafe(reservation?.start || reservation?.pickupAt);
  const end = toIsoStringSafe(reservation?.end || reservation?.dropoffAt);
  if (!start || !end || start >= end) return [];

  const substitutions = parseReservationCarSubstitutions(reservation?.carSubstitutions);
  if (!substitutions.length) {
    const fallback = buildSegment({
      reservation,
      segmentIndex: 0,
      carId: safeStr(reservation?.carId),
      carName: safeStr(reservation?.originalCarName),
      carPlate: safeStr(reservation?.originalCarPlate),
      start,
      end,
      isSubstitution: false,
    });
    return fallback ? [fallback] : [];
  }

  const effectiveSubs = substitutions.filter(
    (entry) => entry.effectiveAt > start && entry.effectiveAt < end
  );

  if (!effectiveSubs.length) {
    const fallback = buildSegment({
      reservation,
      segmentIndex: 0,
      carId: safeStr(reservation?.carId),
      carName: safeStr(reservation?.originalCarName),
      carPlate: safeStr(reservation?.originalCarPlate),
      start,
      end,
      isSubstitution: false,
    });
    return fallback ? [fallback] : [];
  }

  const first = effectiveSubs[0];
  const segments = [];

  const initialSegment = buildSegment({
    reservation,
    segmentIndex: 0,
    carId: first.fromCarId || safeStr(reservation?.carId),
    carName: first.fromCarName || safeStr(reservation?.originalCarName),
    carPlate: first.fromCarPlate || safeStr(reservation?.originalCarPlate),
    start,
    end: first.effectiveAt,
    isSubstitution: false,
    transition: first,
  });
  if (initialSegment) segments.push(initialSegment);

  for (let index = 0; index < effectiveSubs.length; index += 1) {
    const entry = effectiveSubs[index];
    const nextEntry = effectiveSubs[index + 1] || null;
    const segStart = entry.effectiveAt;
    const segEnd = nextEntry?.effectiveAt || end;

    const segment = buildSegment({
      reservation,
      segmentIndex: index + 1,
      carId: entry.toCarId,
      carName: entry.toCarName,
      carPlate: entry.toCarPlate,
      start: segStart,
      end: segEnd,
      isSubstitution: true,
      transition: entry,
    });
    if (segment) segments.push(segment);
  }

  if (!segments.length) {
    const fallback = buildSegment({
      reservation,
      segmentIndex: 0,
      carId: safeStr(reservation?.carId),
      carName: safeStr(reservation?.originalCarName),
      carPlate: safeStr(reservation?.originalCarPlate),
      start,
      end,
      isSubstitution: false,
    });
    return fallback ? [fallback] : [];
  }

  return segments;
}

export function getReservationCurrentCarSegment(reservation) {
  const segments = getReservationCarSegments(reservation);
  if (!segments.length) return null;
  return segments[segments.length - 1];
}

export function getReservationActiveCarAt(reservation, atIso) {
  const target = toIsoStringSafe(atIso);
  const segments = getReservationCarSegments(reservation);
  if (!segments.length) return null;

  if (!target) return segments[segments.length - 1];

  return (
    segments.find((segment) => segment.start <= target && target < segment.end) ||
    segments[segments.length - 1]
  );
}

export function reservationUsesCarInWindow(reservation, carId, startIso, endIso) {
  const targetCarId = safeStr(carId);
  if (!targetCarId || !startIso || !endIso || startIso >= endIso) return false;

  return getReservationCarSegments(reservation).some(
    (segment) =>
      segment.carId === targetCarId && overlapsIso(startIso, endIso, segment.start, segment.end)
  );
}
